import React, { useContext, useEffect, useState } from 'react';
import {
  Modal, Fade, Typography, Grid, Box, Paper,
  Tabs, Tab, useMediaQuery, Card, CardContent
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { SiteContext } from '../../utils/SiteContext';
import { GetWorkOrderLines } from '../../utils/AxiosUtils';


const StyledModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContent = styled(Paper)({
  padding: 24,
  maxHeight: '90vh',
  overflowY: 'auto',
  width: '85%',
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
});

const InfoBlock = ({ labels, values, gridSize }) => (
  <Grid item xs={12} md={gridSize}>
    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', padding: 2, border: '1px solid #ccc', borderRadius: 1, alignItems: 'flex-start' }}>
      <Box sx={{ paddingRight: 1, width: '80px', flexShrink: 0 }}>
        {labels.map((label, index) => (
          <Typography key={index} variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
            {label} 
          </Typography>
        ))}
      </Box>
      <Box>
        {values.map((value, index) => (
          <Typography key={index} variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-line' }}>
            {value || '\n'}
          </Typography>
        ))}
      </Box>
    </Box>
  </Grid>
);

// Function to format description with tracking link if applicable
function formatDescription(desc, shipVia) {
  const description = desc || "";
  const prefixes = ["TRACKING#: ", "RET-TRACK:"];

  // Find a matching prefix
  const prefix = prefixes.find((p) => description.includes(p));
  if (!prefix) return description;

  const index = description.indexOf(prefix);
  const trackingNumber = description.substring(index + prefix.length).trim();

  if (!trackingNumber) return desc;

  // Build tracking URL
  let url = "";
  if (shipVia?.includes("UPS")) {
    url = `https://www.ups.com/track?tracknum=${trackingNumber}`;
  } else if (shipVia?.includes("FEDEX")) {
    url = `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
  }

  // Label depends on prefix
  const label = prefix === "RET-TRACK:" ? "Return Tracking:" : "Tracking#:";

  // return formmatted link or text;
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {label} {trackingNumber}
    </a>
    ) : (
      `${label} ${trackingNumber}`
    );
}

export default function WorkOrderModal({ open, onClose, rowData }) {
  const { loading } = useContext(SiteContext);
  const [, setLoading] = loading;

  const [rowDetail, setRowDetail] = useState([]);
  // const [pageVal, setPage] = useState({ page: 1, pg_size: 20, count: 0 });
  const [tab, setTab] = useState(0);

  const isMobile = useMediaQuery("(max-width: 600px)");

  useEffect(() => {
    if (rowData?.work_order) {
      setLoading(true);

      // Decide which parameter to send
      const orderParam = rowData.status === "COMPLETE" ? null : rowData.work_order;
      const invoiceParam = rowData.status === "COMPLETE" ? rowData.invoice : null;

      GetWorkOrderLines(orderParam, invoiceParam, (data) => {
        setRowDetail(data || []);
        setLoading(false); });
      // }, [pageVal, setPage]);
    }
  }, [rowData]);


  const columns = [
    { field: 'rec', headerName: 'ID', flex: 1 },
    { field: 'item', headerName: 'Item #', flex: 1},
    { 
      field: 'desc', 
      headerName: 'Description', 
      flex: 3,
      renderCell: (params) => {
        return formatDescription(params.row.desc, rowData.ship_via);
      }
    },
    { field: 'qty', headerName: 'Qty', type: 'number', flex: 1 },
    { field: 'iss', headerName: 'Iss', type: 'number', flex: 1 },
    { 
      field: 'price', 
      headerName: 'Price', 
      type: 'number', 
      flex: 1,
      valueFormatter: (params) => {
        const value = Number(params.value) || 0;
        return value.toFixed(2);
      }
    },
    {
        field: 'amount',
        headerName: 'Total',
        type: 'number',
        flex: 1,
        valueGetter: (params) => {
          const qty = Number(params.row.qty) || 0;
          const price = Number(params.row.price) || 0;
          const total = qty * price;
          return total.toFixed(2);
        },
    },

  ];

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContent>
        {rowData ? (
          <>
          {/* Mobile Tabs */}
          {isMobile && (
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab label="Details" />
              <Tab label="Items" />
            </Tabs>
          )}

          {/* PAGE 1 — DETAILS */}
          {(tab === 0 || !isMobile) && (
            <>
              <Grid container spacing={2}>
                <InfoBlock
                  gridSize={3}
                  labels={['Order #:', 'Date:', 'Ship Via:', 'Status:']}
                  values={[rowData.work_order, rowData.wo_date, rowData.ship_via, rowData.status]}
                />
                <InfoBlock
                  gridSize={3}
                  labels={['Invoice #:', 'Inv Date:', 'Terms:', 'Payment:', 'Batch:']}
                  values={[rowData.invoice, rowData.ivh_date, rowData.terms, rowData.payment, rowData.ivh_batch]}
                />
                <InfoBlock
                  gridSize={6}
                  labels={['PO#:', 'Req#:', 'Ref#:', 'Exch Ref#:', 'Ship To:']}
                  values={[rowData.cust_po, rowData.cust_req, rowData.cust_ref, rowData.exch_ref, rowData.ship_to]}
                />
              </Grid>
            </>
          )}

          {/* PAGE 2 — ITEMS TABLE */}
          {(tab === 1 || !isMobile) && (
            <Box sx={{ width: '100%' }}>

              {/* Desktop DataGrid */}
              {!isMobile ? (
                <Box sx={{ mt: 3, height: 400, width: '100%'}}>
                  <DataGrid
                    rows={rowDetail}
                    columns={columns.filter(col => col.field !== 'rec')}
                    getRowId={(row) => row.id}
                    disableSelectionOnClick
                    rowHeight={35}
                    sx={{pr: 2, pl: 2}}
                  />
                </Box>
              ) : (
                <>
                  {/* Mobile Card List */}
                  {rowDetail.map((row) => {
                    const formattedDesc = formatDescription(
                      row.desc,
                      rowData.ship_via
                    );

                    return (
                      <Box sx={{ mb: 2 }} key={row.id}>
                        <InfoBlock
                          gridSize={3}
                          labels={['Item:', 'Qty:', 'Issued:', 'Price:', 'Total:', 'Descr:']}
                          values={[row.item, row.qty, row.iss, Number(row.price).toFixed(2), (row.qty * row.price).toFixed(2), formattedDesc]}
                        />
                      </Box>
                    );  
                  })}
                </>
              )}
              <Box mt={3} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2">Subtotal: {rowData.sub_total ?? '0.00'}</Typography>
                {rowData.status === 'COMPLETE' && (
                  <>
                    <Typography variant="subtitle2">Tax: {rowData.tax ?? '0.00'}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Total: {rowData.total ?? '0.00'}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          </>
        ) : (
          <Typography>Loading work order details...</Typography>
        )}
      </ModalContent>
    </StyledModal>
  );
}
